import {
  eventShiftsSchema,
  minuteLabel,
  SHIFT_ROLE_LABELS,
  shiftCandidateSchema,
  shiftsCopyResultSchema,
  type AdminEvent,
  type EventShifts,
  type EventTask,
  type ShiftCandidate,
  type ShiftDay,
  type ShiftsCopyResult,
} from '@growup/site-shared';
import { Autocomplete, Button, Card, cx, Field, Input, Notice, Select } from '@growup/ui';
import { useState, type FormEvent } from 'react';
import { z } from 'zod';

import { ApiError, getJson, messageOf, send } from '../api';

/** Quem tem a tarefa: a pessoa escolhida, ou quem esta de turno, ou ninguem. */
export function taskOwner(task: EventTask): string {
  if (task.assignee) return task.assignee.login;
  if (task.onShift.length > 0) return task.onShift.join(', ');
  return 'ninguém de turno';
}

/** As tarefas do dia, por hora, com a caixa de feita (RF-E26). */
export function DayTasks({
  base,
  data,
  day,
  onOpen,
  onSaved,
}: {
  base: string;
  data: EventShifts;
  day: ShiftDay;
  onOpen: (taskId: string) => void;
  onSaved: (d: EventShifts) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const zoneName = new Map(data.zones.map((z) => [z.id, z.name]));
  const tasks = data.tasks.filter((t) => t.day === day.day);
  if (tasks.length === 0) return null;

  const mark = async (task: EventTask, done: boolean) => {
    setBusy(true);
    setError(null);
    try {
      onSaved(
        eventShiftsSchema.parse(await send('POST', `${base}/tasks/${task.id}/done`, { done })),
      );
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label="Tarefas do dia" className="flex flex-col gap-2">
      <h4 className="font-sub text-label font-bold uppercase text-gu-ink-muted">Tarefas do dia</h4>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <ul className="flex flex-col">
        {tasks.map((t) => (
          <li key={t.id} className="flex items-center gap-3 border-b border-gu-line py-2">
            <input
              type="checkbox"
              className="size-5 shrink-0 cursor-pointer accent-[rgb(var(--gu-accent))]"
              checked={t.done}
              disabled={busy}
              aria-label={`Feita: ${t.title}`}
              onChange={(e) => void mark(t, e.target.checked)}
            />
            <button
              type="button"
              className="flex min-w-0 flex-1 flex-col text-left"
              onClick={() => onOpen(t.id)}
            >
              <span className={cx('font-semibold', t.done && 'text-gu-ink-muted line-through')}>
                {minuteLabel(t.atMinute)} · {t.title}
              </span>
              <span
                className={cx(
                  'text-caption',
                  t.uncovered && !t.done ? 'text-gu-danger' : 'text-gu-ink-muted',
                )}
              >
                {zoneName.get(t.zoneId) ?? '—'} · {t.uncovered ? '⚠ ' : ''}
                {taskOwner(t)}
                {t.done && t.doneBy ? ` · feita por ${t.doneBy}` : ''}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Horas e minutos de 15 em 15, dentro das horas do dia. */
function minuteOptions(open: number, close: number): number[] {
  const out: number[] = [];
  for (let m = open * 60; m < close * 60; m += 15) out.push(m);
  return out;
}

export function TaskEditor({
  base,
  data,
  task,
  draft,
  onSaved,
  onClose,
}: {
  base: string;
  data: EventShifts;
  task: EventTask | null;
  draft: { zoneId: string; day: string; atMinute: number } | null;
  onSaved: (d: EventShifts) => void;
  onClose: () => void;
}) {
  const [zoneId, setZoneId] = useState(task?.zoneId ?? draft?.zoneId ?? '');
  const [day, setDay] = useState(task?.day ?? draft?.day ?? '');
  const [atMinute, setAtMinute] = useState(task?.atMinute ?? draft?.atMinute ?? 540);
  const [title, setTitle] = useState(task?.title ?? '');
  const [assignee, setAssignee] = useState<ShiftCandidate | null>(task?.assignee ?? null);
  const [query, setQuery] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const dayInfo = data.days.find((d) => d.day === day) ?? data.days[0];
  const options = minuteOptions(dayInfo?.openHour ?? 0, dayInfo?.closeHour ?? 24);
  // Uma hora antiga fora dos quartos de hora continua na lista.
  if (!options.includes(atMinute)) options.push(atMinute);
  options.sort((a, b) => a - b);

  const search = (q: string) =>
    getJson(
      `${base}/people?day=${encodeURIComponent(day)}&q=${encodeURIComponent(q)}`,
      z.array(shiftCandidateSchema),
    );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (title.trim() === '') {
      setFields({ title: 'Falta o título.' });
      return;
    }
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const body = {
        zoneId,
        day,
        atMinute,
        title: title.trim(),
        assigneeAccountId: assignee?.accountId ?? null,
      };
      const res = task
        ? await send('PUT', `${base}/tasks/${task.id}`, body)
        : await send('POST', `${base}/tasks`, body);
      onSaved(eventShiftsSchema.parse(res));
    } catch (err) {
      if (err instanceof ApiError) setFields(err.fields);
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!task || !window.confirm('Apagar esta tarefa?')) return;
    setBusy(true);
    try {
      onSaved(eventShiftsSchema.parse(await send('DELETE', `${base}/tasks/${task.id}`)));
    } catch (err) {
      setError(messageOf(err));
      setBusy(false);
    }
  };

  return (
    <Card headingLevel={3} title={task ? 'Editar tarefa' : 'Nova tarefa'}>
      <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <Field label="Tarefa" error={fields.title}>
          <Input
            value={title}
            maxLength={140}
            placeholder="Abrir o check-in"
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Zona" error={fields.zoneId}>
            <Select value={zoneId} onChange={(e) => setZoneId(e.target.value)}>
              {data.zones.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Dia" error={fields.day}>
            <Select
              value={day}
              onChange={(e) => {
                setDay(e.target.value);
                setAssignee(null);
              }}
            >
              {data.days.map((d) => (
                <option key={d.day} value={d.day}>
                  {d.day.slice(8, 10)}/{d.day.slice(5, 7)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Hora" error={fields.atMinute}>
            <Select value={atMinute} onChange={(e) => setAtMinute(Number(e.target.value))}>
              {options.map((m) => (
                <option key={m} value={m}>
                  {minuteLabel(m)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field
          label="Pessoa"
          optional
          hint="Sem pessoa, a tarefa é de quem está de turno nessa zona a essa hora."
          error={fields.assigneeAccountId}
        >
          {assignee ? (
            <span className="flex items-center gap-2">
              {assignee.login}
              <span className="text-caption text-gu-ink-muted">
                {SHIFT_ROLE_LABELS[assignee.role]}
              </span>
              <button
                type="button"
                aria-label={`Tirar ${assignee.login}`}
                className="text-gu-ink-muted hover:text-gu-ink"
                onClick={() => setAssignee(null)}
              >
                ✕
              </button>
            </span>
          ) : (
            <Autocomplete
              value={query}
              onChange={setQuery}
              search={search}
              getKey={(p) => p.accountId}
              renderItem={(p) => (
                <span className="flex min-w-0 flex-col">
                  <span className="truncate font-semibold">{p.login}</span>
                  <span className="text-caption text-gu-ink-muted">
                    {SHIFT_ROLE_LABELS[p.role]}
                  </span>
                </span>
              )}
              onSelect={(p) => {
                setQuery('');
                setAssignee(p);
              }}
              placeholder="Procurar pelo login…"
            />
          )}
        </Field>
        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={busy}>
            Guardar
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          {task ? (
            <Button variant="ghost" onClick={() => void remove()} disabled={busy}>
              Apagar
            </Button>
          ) : null}
        </div>
      </form>
    </Card>
  );
}

/** Copiar horas, turnos e tarefas de outro evento, sem pessoas. So num evento sem turnos. */
export function CopyShifts({
  base,
  events,
  onCopied,
}: {
  base: string;
  events: AdminEvent[];
  onCopied: (result: ShiftsCopyResult) => void;
}) {
  const [from, setFrom] = useState(events[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (events.length === 0) return null;

  const copy = async (e: FormEvent) => {
    e.preventDefault();
    if (!from) return;
    setBusy(true);
    setError(null);
    try {
      onCopied(
        shiftsCopyResultSchema.parse(await send('POST', `${base}/copy`, { fromEventId: from })),
      );
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="flex flex-wrap items-end gap-3 border-2 border-gu-line p-3"
      onSubmit={(e) => void copy(e)}
    >
      <Field
        label="Copiar os turnos de"
        hint="Copia as horas de cada dia, os turnos e as tarefas, sem pessoas. Os dias vão pela ordem e as zonas pelo nome: copie primeiro o Espaço."
        className="min-w-64 flex-1"
      >
        <Select value={from} onChange={(e) => setFrom(e.target.value)}>
          {events.map((ev) => (
            <option key={ev.id} value={ev.id}>
              {ev.title}
            </option>
          ))}
        </Select>
      </Field>
      <Button type="submit" disabled={busy || !from}>
        Copiar
      </Button>
      {error ? <span className="w-full text-caption text-gu-danger">{error}</span> : null}
    </form>
  );
}

export function copiedLine(result: ShiftsCopyResult): string {
  const { shifts, tasks } = result.copied;
  const parts = [
    `Copiados ${String(shifts)} ${shifts === 1 ? 'turno' : 'turnos'} e ${String(tasks)} ${
      tasks === 1 ? 'tarefa' : 'tarefas'
    }.`,
  ];
  if (result.skipped > 0) {
    parts.push(
      `${String(result.skipped)} ${result.skipped === 1 ? 'ficou' : 'ficaram'} de fora: a zona não existe neste evento, ou o dia não existe.`,
    );
  }
  return parts.join(' ');
}
